import { useCurrencyFormatter } from 'hooks/useNumberFormatters'
import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { EmptyObject } from 'lib/object/objectUtils'
import { SearchMapRedesignContext } from './SearchMapRedesignContext'

const RegisterPriceContext = createContext<((offerId: string, label: string) => () => void) | undefined>(undefined)
export const SearchMapRowPriceOfferContext = createContext<string | undefined>(undefined)
export const SearchMapRowPricesContext = createContext<Readonly<Record<string, string>>>(EmptyObject)

/** Use the price shown in a map row for its pin, including member prices when available. */
export function SearchMapRedesignProvider({ enabled, searchKey, children }: { enabled: boolean; searchKey: string; children: ReactNode }) {
  const [prices, setPrices] = useState<Record<string, { offerId: string; searchKey: string; label: string }>>({})
  const register = useCallback((offerId: string, label: string) => {
    const key = JSON.stringify([searchKey, offerId])
    const entry = { offerId, searchKey, label }
    setPrices(previous => ({ ...previous, [key]: entry }))
    return () => setPrices(previous => {
      if (previous[key] !== entry) return previous
      const next = { ...previous }
      delete next[key]
      return next
    })
  }, [searchKey])
  const currentPrices = useMemo(() => Object.fromEntries(
    Object.values(prices).filter(price => price.searchKey === searchKey).map(price => [price.offerId, price.label]),
  ), [prices, searchKey])

  return <SearchMapRedesignContext.Provider value={enabled}>
    <RegisterPriceContext.Provider value={register}>
      <SearchMapRowPricesContext.Provider value={currentPrices}>{children}</SearchMapRowPricesContext.Provider>
    </RegisterPriceContext.Provider>
  </SearchMapRedesignContext.Provider>
}

export function useReportSearchMapRowPrice(price: number | undefined) {
  const offerId = useContext(SearchMapRowPriceOfferContext)
  const register = useContext(RegisterPriceContext)
  const formatCurrency = useCurrencyFormatter()
  const label = offerId && price !== undefined && Number.isFinite(price) && price > 0 ? formatCurrency(price, 'roundedDollar') : undefined
  useEffect(() => {
    if (offerId && label && register) return register(offerId, label)
  }, [label, offerId, register])
}
